import moment from 'moment'
import _ from 'lodash'
import formatValue from 'scoreboard/components/format-value'
import momentHelper from 'scoreboard/components/moment-helper'
import DataType from 'scoreboard/datasets/model/data-type'
const { TIME } = DataType

/**
 * @constant {string}
 */
const DATE_FORMAT = 'L'
// ISO 8601 format (YYYY-MM-DDTHH:mm:ss.sssZ)
const ISO_8601_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/
// 24-hour time format (ex. HH:mm:ss GMT-0600 (Central Standard Time))
const TIME_REGEX = /^(\d{2}:\d{2}:\d{2}) GMT[+\-]\d{4} \(.*\)$/
// locale-specific date and time format (ex. MM/DD/YYYY, HH:mm:ss AM/PM)
const LOCALE_REGEX = /^\d{1,2}\/\d{1,2}\/\d{4}, \d{1,2}:\d{2}:\d{2} (AM|PM)$/
// Date-time format (YYYY-MM-DDTHH:mm:ss)
const DATE_TIME_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/
// Date format (YYYY-MM-DD)
const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/

/**
 * An object representing a time, split out into its constituent fields, the
 * way it is entered into inputs in the UI. Can represent 12 or
 * 24 hour time.
 * @typedef {{
 * 		period: 'AM'|'PM' | '' | undefined,
 * 		hour: string | number,
 * 		minute: string | number,
 * 		second: string | number
 * 	}} SplitFieldsTime
 */

/**
 * An object representing a date and/or time, split out into its constituent
 * fields, the way it is entered into inputs in the UI. Can also include the
 * moment representation of the other fields.
 * @typedef {{
 * 	date: string | undefined,
 * 	time: SplitFieldsTime | undefined,
 * 	moment: moment.Moment | undefined
 * }} SplitFieldsDateTime
 */

/**
 * @param {SplitFieldsDateTime} splitObject
 * @returns {moment.Moment}
 */
function splitFieldsObjectToDateTime(splitObject) {
	splitObject.time = splitObject.time || {
		hour: 0,
		minute: 0,
		second: 0
	}
	let t = {
		date: splitObject.date,
		hour: splitObject.time.hour,
		minute: splitObject.time.minute,
		second: splitObject.time.second,
		period: splitObject.time.period || ''
	}

	const timeString = to24hrTime(splitObject.time)

	return moment(new Date(`${t.date} ${timeString}`))
}

/**
 * @param timeString a time string in either 12 or 24 hr format, that is
 * HH:mm:ss or hh:mm:ss A
 * @param hour24 {boolean} whether to output in 24 hr format
 * @returns {SplitFieldsTime}
 */
function timeToSplitFieldsObject(timeString, hour24 = false) {
	if (!timeString) return null

	const parts = timeString.split(':').map(it => parseInt(it))
	const parsed = {
		hour: parts[0],
		minute: parts[1],
		second: parts[2]
	}
	if (hour24) return parsed
	else {
		let period = 'AM'
		if (parsed.hour === 24 || parsed.hour === 0) {
			parsed.hour = 12
		} else if (parsed.hour === 12) {
			period = 'PM'
		} else if (parsed.hour > 12) {
			period = 'PM'
			parsed.hour -= 12
		}
		return _.extend(parsed,{ period })
	}
}

/**
 * @param dateTime {string|moment.Moment} if a string, then anything moment can
 * parse
 * @param hour24 whether to express the output in 24 hour time
 * @param fromZone the time zone associated with the dateTime passed in
 * @returns {SplitFieldsDateTime}
 */
function dateTimeToSplitFieldsObject(
	dateTime, fromZone = momentHelper.defaultTimeZone(), hour24 = false
) {
	const asMoment = moment.tz(dateTime, undefined, fromZone).tz(momentHelper.defaultTimeZone())
	return {
		date: asMoment.format(DATE_FORMAT),
		time: {
			hour: hour24 ? asMoment.format('HH') : asMoment.format('hh'),
			minute: asMoment.format('mm'),
			second: asMoment.format('ss'),
			period: hour24 ? undefined : asMoment.format('A')
		},
		moment: asMoment
	}
}

/**
 * @param time {SplitFieldsTime | string} If a string, needs to be a
 * recognizable time string.
 * Formats a time into a display value
 * @param timeDisplayType {TimeDisplayType} 12 or 24, seconds or no seconds
 */
function getTimeString(time, timeDisplayType) {
	const timeFormat = momentHelper.getTimeFormat(timeDisplayType)
	return formatValue({
		// Internal, string representations are always 24hr.
		value: to24hrTime(time),
		dataType: TIME,
		timeFormat
	})
}

/**
 * @param {SplitFieldsDateTime} dateTime
 * @param {TimeDisplayType} timeDisplayType how to format the time portion
 * @param {boolean} alwaysDate Whether put the current date in the output string
 * if no date is provided in [dateTime]
 * @return {String} In the format [{date} ]{time} - where time is always
 * there but date might not be depending on the value of alwaysDate. The
 * date will be formatted according to {DATE_FORMAT}, and time
 * will be formatted as hh:mm:dd {AM/PM} with all zeros if no input time was
 * provided
 */
function getDateTimeString(
	dateTime,
	timeDisplayType,
	alwaysDate = false
) {
	if (!dateTime) {
		return null
	}

	if (alwaysDate && !dateTime.date) {
		dateTime.date = moment().format(DATE_FORMAT)
	}

	dateTime.time = dateTime.time || {
		hour: 0,
		minute: 0,
		second: 0
	}

	const stringTime = getTimeString(dateTime.time, timeDisplayType)
	return [dateTime.date, stringTime].filter(it => !!it).join(' ')
}

/**
 * @param {SplitFieldsTime | string} time.
 * If a string, then a time string in either 12 or 24 hr
 * format, zero padded or non-zero padded. e.x. 08:45:00 AM or 8:45:0.
 * Always produces the zero-padded 24 hr equivalent of the input. Used to
 * represent the internal state of a time value because this is what the
 * server-side knows how to deserialize.
 * @returns {string} The 24 hour representation of the input - which could be
 * the same as the input, if the input was already in zero-padded, 24 hour time.
 */
const to24hrTime = function(time) {
	if (_.isEmpty(time)) return undefined
	
	const hasHour = !_.isNil(time.hour)
	const t = typeof time === 'string' ? parseTimeString(time) : {
		hour: (hasHour && parseInt(time.hour)) || 0,
		minute: (hasHour && parseInt(time.minute)) || 0,
		second: (hasHour && parseInt(time.second)) || 0,
		period: time.period
	}
	const adjustHours = (hours, period) => {
		const safePeriod = (period || '').toUpperCase()
		switch (safePeriod) {
			// #35380: 24-hundred hours is a valid synonym for 0-hundred hours, but
			// because Java understands 24 hour fields as strictly zero-based,
			// with 23 being the max out of 24 possible values, it will not parse.
			case 'PM': if (hours === 12) return hours; else return hours + 12
			case 'AM': if (hours === 12) return 0
			default: return hours
		}
	}
	return [adjustHours(t.hour, t.period), t.minute, t.second]
		.map(it => `${it}`.padStart(2, '0'))
		.join(':')
}


/**
 * @param timeString
 * @returns {SplitFieldsTime}
 * Parse a time string into its constituent fields. Interpretation and
 * processing to be done elsewhere.
 */
const parseTimeString = function(timeString) {
	const [digits, period] = timeString.split(' ')
	const [hour, minute, second] = digits.split(':').map(it => parseInt(it))
	return { hour, minute, second: second || 0, period }
}

export {
	splitFieldsObjectToDateTime,
	timeToSplitFieldsObject,
	dateTimeToSplitFieldsObject,
	getTimeString,
	getDateTimeString,
	to24hrTime,
	DATE_FORMAT,
	ISO_8601_REGEX,
	TIME_REGEX,
	LOCALE_REGEX,
	DATE_TIME_REGEX,
	DATE_REGEX
}